import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface JournalDescriptionEditDialogData {
	journalNumber: string | number;
	date: string;
	currentNarrative: string;
	hasReversal: boolean;
	reversalJournalNumber?: number | null;
}

export interface JournalDescriptionEditDialogResult {
	saved: boolean;
	narrative: string;
	updateReversal: boolean;
}

@Component({
	selector: 'app-journal-description-edit-dialog',
	template: `
		<h2 mat-dialog-title>
			Edit Journal Description
			<span class="muted-title">Journal #{{ data.journalNumber }}</span>
		</h2>
		<mat-dialog-content>
			<p class="text-muted mb-2" *ngIf="data.date">
				Date: {{ data.date | date }}
			</p>
			<form [formGroup]="form">
				<mat-form-field appearance="outline" class="full-width">
					<mat-label>Description</mat-label>
					<textarea
						matInput
						formControlName="narrative"
						rows="3"
						placeholder="Journal description..."
					></textarea>
				</mat-form-field>
				<div *ngIf="data.hasReversal" class="mt-2">
					<mat-checkbox formControlName="updateReversal" color="primary">
						Also update reversal
						<span *ngIf="data.reversalJournalNumber">
							(Journal #{{ data.reversalJournalNumber }})
						</span>
					</mat-checkbox>
					<div class="hint text-muted small">
						The reversal will be rewritten as
						<em>"Reversal of Journal #{{ data.journalNumber }}: &lt;new description&gt;"</em>.
					</div>
				</div>
				<p class="text-muted small mt-2 mb-0">
					This updates the description on every line of the journal.
				</p>
			</form>
		</mat-dialog-content>
		<mat-dialog-actions align="end">
			<button mat-stroked-button (click)="cancel()">Cancel</button>
			<button
				mat-raised-button
				color="primary"
				[disabled]="form.invalid"
				(click)="save()"
			>
				Save
			</button>
		</mat-dialog-actions>
	`,
	styles: [
		`
			.full-width { width: 100%; }
			.muted-title { color: rgba(0, 0, 0, 0.6); font-weight: 400; margin-left: 6px; font-size: 0.9em; }
			.hint { margin-top: 4px; }
		`
	]
})
export class JournalDescriptionEditDialogComponent {
	form: FormGroup;

	constructor(
		public dialogRef: MatDialogRef<JournalDescriptionEditDialogComponent, JournalDescriptionEditDialogResult>,
		@Inject(MAT_DIALOG_DATA) public data: JournalDescriptionEditDialogData,
		private fb: FormBuilder
	) {
		this.form = this.fb.group({
			narrative: [this.data.currentNarrative ?? ''],
			updateReversal: [this.data.hasReversal]
		});
	}

	cancel(): void {
		this.dialogRef.close();
	}

	save(): void {
		const narrative = String(this.form.get('narrative')?.value ?? '').trim();
		const updateReversal = !!this.form.get('updateReversal')?.value && this.data.hasReversal;
		this.dialogRef.close({ saved: true, narrative, updateReversal });
	}
}
